import { RefObject } from 'react';

import { ErrorEventsEnum } from '@northladder/utilities';
import { logErrorToSentry } from '@northladder/web-utils';

import {
  configureAudioForMobile,
  playAudioUntilEnded,
  setupAudioWithWebAudio,
  waitForAudioCanPlay,
} from './audioPlaybackUtils';
import { createAndResumeAudioContext } from './audioContextUtils';
import { isMobileDevice } from './deviceDetectionUtils';
import { playPreloadedAudio, preloadAudio } from './preloadedAudio';
import { findWorkingAudioFormat } from './audioFormatUtils';

/**
 * Plays audio using HTMLAudioElement with Web Audio API fallback
 */
async function playAudioWithFallback(
  referenceAudioPath: string,
  audioContextForPlayback: AudioContext,
  audioRef: RefObject<HTMLAudioElement | null>,
  errorMessage: string
): Promise<void> {
  let workingAudioPath = referenceAudioPath;

  try {
    const foundPath = await findWorkingAudioFormat(referenceAudioPath);

    if (foundPath) {
      workingAudioPath = foundPath;
    }
  } catch (error) {
    logErrorToSentry({
      error: error instanceof Error ? error : new Error(String(error)),
      errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
      errorName: 'AUDIO_FORMAT_FALLBACK_FAILED',
      source: 'audioPlaybackSetup.playAudioWithFallback',
    });
  }

  const audio = new Audio(workingAudioPath);
  // eslint-disable-next-line no-param-reassign
  audioRef.current = audio;

  audio.volume = 1.0;

  if (isMobileDevice()) {
    configureAudioForMobile(audio);
  }

  audio.load();

  await waitForAudioCanPlay(audio);

  if (audioContextForPlayback) {
    try {
      await setupAudioWithWebAudio(audio, audioContextForPlayback, 2.0);
    } catch (webAudioError) {
      logErrorToSentry({
        error:
          webAudioError instanceof Error
            ? webAudioError
            : new Error(String(webAudioError)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'WEB_AUDIO_API_FAILED',
        source: 'audioPlaybackSetup.playAudioWithFallback',
      });
    }
  }

  await playAudioUntilEnded(audio, 2, errorMessage);
}

/**
 * Sets up and plays audio using preloaded buffer or fallback to HTMLAudioElement
 */
export async function setupAndPlayAudio(
  referenceAudioPath: string,
  audioContextRef: RefObject<AudioContext | null>,
  audioBufferRef: RefObject<AudioBuffer | null>,
  audioRef: RefObject<HTMLAudioElement | null>,
  errorMessage: string
): Promise<void> {
  let audioContextForPlayback = audioContextRef.current;
  let newlyCreatedContext: AudioContext | null = null;

  if (!audioContextForPlayback) {
    newlyCreatedContext = await createAndResumeAudioContext();
    audioContextForPlayback = newlyCreatedContext;
    // eslint-disable-next-line no-param-reassign
    audioContextRef.current = audioContextForPlayback;
  }

  if (!audioContextForPlayback) {
    throw new Error('AudioContext is not available');
  }

  if (!audioBufferRef.current) {
    try {
      // eslint-disable-next-line no-param-reassign
      audioBufferRef.current = await preloadAudio(
        referenceAudioPath,
        audioContextForPlayback
      );
    } catch (error) {
      logErrorToSentry({
        error: error instanceof Error ? error : new Error(String(error)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'AUDIO_PRELOAD_FAILED',
        source: 'audioPlaybackSetup.setupAndPlayAudio',
      });
    }
  }

  if (audioContextForPlayback && audioBufferRef.current) {
    try {
      if (audioContextForPlayback.state === 'suspended') {
        await audioContextForPlayback.resume();
      }

      await playPreloadedAudio(
        audioContextForPlayback,
        audioBufferRef.current,
        2.0
      );
    } catch (preloadedError) {
      logErrorToSentry({
        error:
          preloadedError instanceof Error
            ? preloadedError
            : new Error(String(preloadedError)),
        errorKey: ErrorEventsEnum.ERROR_IN_PERMISSION,
        errorName: 'PRELOADED_AUDIO_PLAYBACK_FAILED',
        source: 'audioPlaybackSetup.setupAndPlayAudio',
      });

      await playAudioWithFallback(
        referenceAudioPath,
        audioContextForPlayback,
        audioRef,
        errorMessage
      );
    }
  } else {
    await playAudioWithFallback(
      referenceAudioPath,
      audioContextForPlayback,
      audioRef,
      errorMessage
    );
  }
}
